import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAttention,
  fetchBudgetAlerts,
  fetchCostEfficiency,
  fetchSpend,
  fetchSummary,
} from '../../../lib/api/analytics';
import { fetchInsights } from '../../../lib/api/insights';
import { InsightsCard } from '../InsightsCard';
import type { AnalyticsRange } from '../../../../../shared/types/analytics';
import type { InsightsRange } from '../../../../../shared/lib/insights';

const POLL_MS = 60_000;

// ---------------------------------------------------------------------------
// UX-02 — Overview tab: 4 KPI tiles + the Forge Wrapped card (moved from
// pages/insights.tsx). The tiles are new (per the spec), built from
// endpoints the other panels already use.
// ---------------------------------------------------------------------------

/** The Insights card's API only supports 7d/30d/all — 90d folds into
 *  "all", noted with a "(all time)" subtitle so it's not silently wrong. */
function toInsightsRange(since: AnalyticsRange): InsightsRange {
  if (since === '7d' || since === '30d') return since;
  return 'all';
}

const RANGE_DAYS: Record<AnalyticsRange, number> = { '7d': 7, '30d': 30, '90d': 90, all: 3650 };

function sinceIsoFor(range: AnalyticsRange): string {
  return new Date(Date.now() - RANGE_DAYS[range] * 24 * 60 * 60 * 1000).toISOString();
}

/** The equal-length window immediately before the current one, for the
 *  Spend tile's "vs previous period" delta. No prior window for "all". */
function previousPeriodRange(since: AnalyticsRange): { from: string; to: string } | null {
  if (since === 'all') return null;
  const days = RANGE_DAYS[since];
  const msDay = 24 * 60 * 60 * 1000;
  const now = Date.now();
  return {
    from: new Date(now - 2 * days * msDay).toISOString(),
    to: new Date(now - days * msDay).toISOString(),
  };
}

const usd = (n: number): string => `$${n.toFixed(2)}`;
const usd3 = (n: number): string => `$${n.toFixed(3)}`;

export const InsightsOverviewPanel: React.FC<{
  since: AnalyticsRange;
  project: string | undefined;
  account?: string;
}> = ({ since, project, account }) => {
  const insightsRange = toInsightsRange(since);
  const insightsQ = useQuery({
    queryKey: ['insights', insightsRange],
    queryFn: () => fetchInsights(insightsRange),
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });

  const prevRange = previousPeriodRange(since);

  const spendCurQ = useQuery({
    queryKey: ['spend', 'kpi', 'current', since, project, account],
    queryFn: () => fetchSpend({ since, project, account }),
  });
  const spendPrevQ = useQuery({
    queryKey: ['spend', 'kpi', 'previous', since, project, account],
    queryFn: () => fetchSpend({ from: prevRange!.from, to: prevRange!.to, project, account }),
    enabled: prevRange !== null,
  });

  const sinceIso = sinceIsoFor(since);
  // Same as EfficiencyPanel: the endpoint's records don't carry a project
  // id, so filtering happens client-side over the unfiltered window
  // (org-prefix heuristic), not via a `project` query param.
  const effQ = useQuery({
    queryKey: ['cost-efficiency', 'kpi', since, account],
    queryFn: () => fetchCostEfficiency({ since: sinceIso, limit: 500, account }),
  });

  const summaryQ = useQuery({
    queryKey: ['analytics', 'summary', 'kpi', since, project, account],
    queryFn: () => fetchSummary({ since, project, account }),
  });

  const attentionQ = useQuery({
    queryKey: ['analytics', 'attention', 'kpi', account],
    queryFn: () => fetchAttention({ account }),
  });
  const budgetQ = useQuery({
    queryKey: ['analytics', 'budget-alerts', 'kpi', account],
    queryFn: () => fetchBudgetAlerts({ account }),
  });

  // Cost / SP: sum(cost) / sum(story points) over the window — same ratio
  // EfficiencyCard uses per-epic, applied in aggregate here.
  const epics = (effQ.data?.epics ?? []).filter((e) => {
    if (!project) return true;
    return e.repo.startsWith(`${project}/`) || e.repo.split('/')[0] === project;
  });
  const totalCost = epics.reduce((a, e) => a + e.epicTotalCostUsd, 0);
  const totalSp = epics.reduce((a, e) => a + e.totalStoryPoints, 0);
  const costPerSp = totalSp > 0 ? totalCost / totalSp : null;

  const curTotal = spendCurQ.data?.grandTotalCostUsd;
  const prevTotal = spendPrevQ.data?.grandTotalCostUsd;
  const deltaPct =
    curTotal != null && prevTotal != null && prevTotal > 0
      ? Math.round(((curTotal - prevTotal) / prevTotal) * 100)
      : null;

  // "Needs attention" — same two sources health.tsx's Reliability tab
  // shows (attention items + budget alerts), summed. (The spec also names
  // `/activity/pending`, but no such endpoint exists in this codebase yet —
  // see PROGRESS.md notes for UX-02.)
  const attentionCount = (attentionQ.data?.items.length ?? 0) + (budgetQ.data?.items.length ?? 0);

  return (
    <div data-testid="insights-overview-panel" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="overview-kpi-tiles">
        <KpiTile
          testid="kpi-spend"
          label="Spend"
          isLoading={spendCurQ.isLoading}
          isError={spendCurQ.isError}
          value={curTotal != null ? usd(curTotal) : undefined}
          sub={
            deltaPct != null
              ? `${deltaPct >= 0 ? '▲' : '▼'} ${Math.abs(deltaPct)}% vs prev period`
              : undefined
          }
        />
        <KpiTile
          testid="kpi-cost-per-sp"
          label="Cost / SP"
          isLoading={effQ.isLoading}
          isError={effQ.isError}
          value={costPerSp != null ? usd3(costPerSp) : '—'}
        />
        <KpiTile
          testid="kpi-story-success"
          label="Story success"
          isLoading={summaryQ.isLoading}
          isError={summaryQ.isError}
          value={summaryQ.data ? `${(summaryQ.data.rates.completionRate * 100).toFixed(1)}%` : undefined}
          sub="first-pass rate"
        />
        <KpiTile
          testid="kpi-needs-attention"
          label="Needs attention"
          isLoading={attentionQ.isLoading || budgetQ.isLoading}
          isError={attentionQ.isError || budgetQ.isError}
          value={String(attentionCount)}
          href="/today"
          sub="open Today"
        />
      </div>

      {insightsQ.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {insightsQ.isError && (
        <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
          {(insightsQ.error as Error).message}
        </div>
      )}
      {insightsQ.isSuccess && insightsQ.data && (
        <InsightsCard
          data={insightsQ.data}
          range={insightsRange}
          onRange={() => {}}
          hideRangeToggle
          subtitle={since === '90d' ? '(all time)' : undefined}
        />
      )}
    </div>
  );
};

const KpiTile: React.FC<{
  testid: string;
  label: string;
  isLoading: boolean;
  isError: boolean;
  value: string | undefined;
  sub?: string;
  href?: string;
}> = ({ testid, label, isLoading, isError, value, sub, href }) => {
  const body = (
    <div
      className="bg-white border border-slate-200 rounded-xl shadow-card px-4 py-3"
      data-testid={testid}
    >
      <div className="text-2xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      {isLoading ? (
        <div
          className="h-6 w-16 bg-slate-100 rounded animate-pulse mt-1.5"
          data-testid={`${testid}-skeleton`}
          aria-label={`Loading ${label}`}
        />
      ) : (
        <div className="text-xl font-semibold text-slate-900 mt-0.5 tabular-nums">
          {isError ? '—' : value ?? '—'}
        </div>
      )}
      {sub && !isLoading && <div className="text-2xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
  if (!href) return body;
  return (
    <Link to={href} className="block rounded-xl hover:shadow-card-hover transition-shadow">
      {body}
    </Link>
  );
};

export default InsightsOverviewPanel;
